import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { Role } from '@revcall/shared';
import { useAuthStore } from '@/stores/authStore';
import { SessionUnavailable } from './SessionUnavailable';

/**
 * RequireRole — wraps a subtree so it only renders when the current
 * user has a matching role on the active Organisation (or is super admin).
 *
 * Renders a redirect to `/unauthorized` if the check fails. Use in the
 * React Router route definitions:
 *
 *   <Route path="/settings/members"
 *          element={<RequireRole roles={['tenant_admin']}><Members/></RequireRole>} />
 *
 * Backend still enforces the real guard — this is UX only.
 */
interface Props {
  roles: Role[];
  /** Where to redirect if the check fails. Defaults to /unauthorized. */
  redirectTo?: string;
  children: ReactNode;
}

export function RequireRole({ roles, redirectTo = '/unauthorized', children }: Props) {
  const user = useAuthStore((s) => s.user);
  const active = useAuthStore((s) => s.activeMembership);
  const isLoading = useAuthStore((s) => s.isLoading);
  const unavailable = useAuthStore((s) => s.unavailable);
  const location = useLocation();

  // Wait for hydration to settle before deciding. Only show the spinner
  // while /auth/me is still in flight — once it returns (with success OR
  // error), we must stop spinning and act.
  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin" />
      </div>
    );
  }

  // 429/5xx/network: the session is probably fine, offer a retry (RUI-13).
  if (unavailable) return <SessionUnavailable />;

  // Hydration finished but no user → session expired / cookie invalidated
  // (e.g. after a redeploy with a fresh JWT secret or a password reset).
  // Redirect to login so the user can reconnect rather than spin forever.
  // No user and no error is an anonymous visitor: same way, and `next` is kept
  // either way so signing in returns to the page asked for (QA-W6-11).
  if (user === null) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/Login?next=${next}`} replace />;
  }

  if (user.isSuperAdmin) {
    return <>{children}</>;
  }

  if (!active || !roles.includes(active.role)) {
    // The page asked for travels to /unauthorized, which names it (B3-unauthorized).
    return <Navigate to={redirectTo} replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
